/**
 * Footer — TASKS 4.1, 6.3ב · DESIGN.md 5.10.
 *
 * ⭐ שלוש עמודות RTL: לוגו+תיאור (start) · ניווט · יצירת קשר (end). שורה
 *    תחתונה נפרדת: שם החברה + ע.מ. (start) · שלושת הקישורים המשפטיים (end).
 * ⛔ אין תנועה — "ה-Footer סטטי בכל הווריאציות" (DESIGN.md 5.10).
 */
import { Link } from 'react-router-dom';
import { EnvelopeSimple, Phone, WhatsappLogo } from '@phosphor-icons/react';
import { useCookieConsent } from '../consent/CookieConsentContext.js';
import {
  BUSINESS_NUMBER,
  COMPANY_NAME,
  COMPANY_TAGLINE,
  EMAIL,
  EMAIL_HREF,
  LOGO_HEIGHT,
  LOGO_WIDTH,
  PHONE_DISPLAY,
  PHONE_TEL_HREF,
  PHONE_WHATSAPP_HREF,
} from '../content/businessInfo.js';
import { NAV_LINKS } from '../content/navLinks.js';

export function Footer() {
  const year = new Date().getFullYear();
  const { openManager } = useCookieConsent();

  return (
    <footer className="bg-surface border-line mt-16 border-t">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-8 sm:grid-cols-3 md:py-12">
        <div className="order-3 sm:order-1">
          {/* ⭐ עודכן 21.08.2026 (פידבק אריאנה, בצפייה חיה בסבב קודם + הגדלה
              נוספת עכשיו) — נמדד בפועל (Playwright, Range על טקסט הטאגליין):
              רוחב התווים בפועל של "יבואנית ויצרנית פתרונות למערכות מיזוג
              אוויר" הוא כ-249px (⛔ לא רוחב העמודה המלא - ה-<p> נמתח לרוחב
              ההורה, זה לא רוחב הטקסט עצמו). w-64=256px הוא הצעד הכי קרוב
              בסולם הקיים. */}
          <Link to="/">
            <img
              src="/brand/logo.png"
              alt={COMPANY_NAME}
              loading="lazy"
              width={LOGO_WIDTH}
              height={LOGO_HEIGHT}
              className="h-auto w-64"
            />
          </Link>
          <p className="text-muted mt-3 text-sm">{COMPANY_TAGLINE}</p>
        </div>

        <div className="order-2">
          <h2 className="text-ink text-[0.95rem] font-medium">ניווט</h2>
          <ul className="mt-3 space-y-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-link hover:decoration-link inline-flex min-h-11 items-center underline decoration-transparent underline-offset-4 transition-[text-decoration-color] duration-150"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="order-1 sm:order-3">
          <h2 className="text-ink text-[0.95rem] font-medium">יצירת קשר</h2>
          <ul className="mt-3 space-y-1">
            <li>
              <a
                href={PHONE_TEL_HREF}
                className="text-link inline-flex min-h-11 items-center gap-2"
              >
                <Phone
                  size={16}
                  weight="regular"
                  aria-hidden="true"
                  style={{ color: 'var(--color-copper)' }}
                />
                <span className="ltr-inline">{PHONE_DISPLAY}</span>
              </a>
            </li>
            <li>
              <a href={EMAIL_HREF} className="text-link inline-flex min-h-11 items-center gap-2">
                <EnvelopeSimple
                  size={16}
                  weight="regular"
                  aria-hidden="true"
                  style={{ color: 'var(--color-copper)' }}
                />
                <span className="ltr-inline">{EMAIL}</span>
              </a>
            </li>
            <li>
              <a
                href={PHONE_WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary mt-2 text-sm"
              >
                <WhatsappLogo size={16} weight="regular" aria-hidden="true" />
                שיחת וואטסאפ
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-line mx-auto flex max-w-6xl flex-col gap-2 border-t px-6 py-4 text-xs sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted">
          © {year} {COMPANY_NAME}, ע.מ. {BUSINESS_NUMBER}. כל הזכויות שמורות.
        </p>
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          <li>
            <Link
              to="/terms"
              className="text-link hover:decoration-link underline decoration-transparent underline-offset-4 transition-[text-decoration-color] duration-150"
            >
              תנאי שימוש
            </Link>
          </li>
          <li>
            <Link
              to="/privacy"
              className="text-link hover:decoration-link underline decoration-transparent underline-offset-4 transition-[text-decoration-color] duration-150"
            >
              מדיניות פרטיות
            </Link>
          </li>
          <li>
            <Link
              to="/accessibility"
              className="text-link hover:decoration-link underline decoration-transparent underline-offset-4 transition-[text-decoration-color] duration-150"
            >
              הצהרת נגישות
            </Link>
          </li>
          <li>
            {/* ⭐ 27.08.2026 — פתיחת פאנל "העדפות עוגיות" מכל עמוד (דרישת
                שקיפות: אפשר לשנות הסכמה בכל עת). כפתור, ⛔ לא Link — אין
                נתיב, זה פותח דיאלוג. מעוצב זהה לקישורים שלצידו. */}
            <button
              type="button"
              onClick={openManager}
              className="text-link hover:decoration-link inline cursor-pointer border-0 bg-transparent p-0 text-xs [font-family:inherit] underline decoration-transparent underline-offset-4 transition-[text-decoration-color] duration-150"
            >
              העדפות עוגיות
            </button>
          </li>
        </ul>
      </div>

      {/* ⭐ קרדיט לבונה האתר — Ariana-Prj CLAUDE.md: "חובה לציין מייל זה בכל
          קובץ או תוכן שנוצר תחת השם Ariana-Prj". */}
      <div className="border-line mx-auto max-w-6xl border-t px-6 py-3 text-center text-xs">
        <p className="text-muted">
          {/* ⚠️ underline — אותה מחלקת באג שכבר תועדה בפירורי הלחם ובדפים
              המשפטיים: קישור נחושת בתוך טקסט אפור, ניגודיות 1.21:1 מתחת
              לסף 3:1 (WCAG 1.4.1 link-in-text-block). נתפס ב-`expectNoA11yViolations`. */}
          אתר זה נבנה ע"י אריאנה ·{' '}
          <a href="mailto:ariana@ariana-prj.com" className="text-link underline">
            ariana@ariana-prj.com
          </a>
        </p>
      </div>
    </footer>
  );
}
